import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://nerdnation.world';
const API_URL = process.env.REACT_APP_BACKEND_URL || BASE_URL;
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;
const SITE_NAME = 'Nerd Nation';

/**
 * Build a dynamic OG image URL from the backend /api/og endpoint.
 * Falls back gracefully if title/subtitle missing.
 */
const buildOgImage = (title, description, ogType) => {
  if (!title) return DEFAULT_IMAGE;
  const params = new URLSearchParams({
    title: String(title).slice(0, 120),
    subtitle: String(description || '').slice(0, 180),
    type: ogType || 'default',
  });
  return `${API_URL}/api/og?${params.toString()}`;
};

/**
 * SEOHead — drop into any page component to set title, description & OG tags.
 * Props:
 *   title       – page title (appended with " | Nerd Nation")
 *   description – 150-160 char description
 *   image       – explicit og:image URL (overrides dynamic generator)
 *   ogType      – content type for dynamic OG image: 'event' | 'job' | 'scholarship' | 'course' | 'exam' | 'default'
 *                 When provided, an auto-generated /api/og image is used.
 *   path        – canonical path e.g. "/scholarships"
 *   noIndex     – true for auth-only / admin pages (noindex,nofollow)
 */
const SEOHead = ({
  title,
  description,
  image,
  ogType,
  path = '',
  noIndex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Your Ambition is Our Mission`;
  const canonical = `${BASE_URL}${path}`;
  // Priority: explicit image prop > dynamic generator (if ogType set) > default
  const resolvedImage =
    image || (ogType ? buildOgImage(title, description, ogType) : DEFAULT_IMAGE);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={resolvedImage} />
    </Helmet>
  );
};

export default SEOHead;
